import type { Letter } from './pitch';

export interface KeySignature {
  /** VexFlow's identifier for the signature. */
  id: string;
  /** Major tonic, e.g. "E♭". */
  major: string;
  /** Relative minor tonic, e.g. "Cm". */
  minor: string;
  /** How many accidentals, and which kind. */
  count: number;
  kind: 'natural' | 'sharp' | 'flat';
  /** Pitch class of the major tonic, 0 = C. Used to order the list chromatically. */
  semitone: number;
  /** Letters altered by the signature. */
  map: Partial<Record<Letter, number>>;
}

/** The order accidentals are always written in. */
const SHARP_ORDER: Letter[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const FLAT_ORDER: Letter[] = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];

function sharps(n: number): Partial<Record<Letter, number>> {
  return Object.fromEntries(SHARP_ORDER.slice(0, n).map((l) => [l, 1]));
}
function flats(n: number): Partial<Record<Letter, number>> {
  return Object.fromEntries(FLAT_ORDER.slice(0, n).map((l) => [l, -1]));
}

function key(
  id: string,
  major: string,
  minor: string,
  semitone: number,
  count: number,
  kind: 'natural' | 'sharp' | 'flat',
): KeySignature {
  return {
    id,
    major,
    minor,
    count,
    kind,
    semitone,
    map: kind === 'sharp' ? sharps(count) : kind === 'flat' ? flats(count) : {},
  };
}

/**
 * All fifteen major key signatures, ordered chromatically by tonic rather than
 * around the circle of fifths. Enharmonic pairs therefore sit next to each other
 * — C♯ beside D♭, F♯ beside G♭, B beside C♭ — which makes the trade-off between
 * seven sharps and five flats visible at the moment you choose one.
 */
export const KEY_SIGNATURES: KeySignature[] = [
  key('C', 'C', 'Am', 0, 0, 'natural'),
  key('Db', 'D♭', 'B♭m', 1, 5, 'flat'),
  key('C#', 'C♯', 'A♯m', 1, 7, 'sharp'),
  key('D', 'D', 'Bm', 2, 2, 'sharp'),
  key('Eb', 'E♭', 'Cm', 3, 3, 'flat'),
  key('E', 'E', 'C♯m', 4, 4, 'sharp'),
  key('F', 'F', 'Dm', 5, 1, 'flat'),
  key('F#', 'F♯', 'D♯m', 6, 6, 'sharp'),
  key('Gb', 'G♭', 'E♭m', 6, 6, 'flat'),
  key('G', 'G', 'Em', 7, 1, 'sharp'),
  key('Ab', 'A♭', 'Fm', 8, 4, 'flat'),
  key('A', 'A', 'F♯m', 9, 3, 'sharp'),
  key('Bb', 'B♭', 'Gm', 10, 2, 'flat'),
  key('B', 'B', 'G♯m', 11, 5, 'sharp'),
  key('Cb', 'C♭', 'A♭m', 11, 7, 'flat'),
];

export const KEY_BY_ID: Record<string, KeySignature> = Object.fromEntries(
  KEY_SIGNATURES.map((k) => [k.id, k]),
);

/** "4♯" / "3♭" / "no accidentals" — the short form shown beside the key name. */
export function accidentalSummary(signature: KeySignature): string {
  if (signature.count === 0) return 'no accidentals';
  return `${signature.count}${signature.kind === 'sharp' ? '♯' : '♭'}`;
}

export function keyLabel(signature: KeySignature): string {
  return `${signature.major} major / ${signature.minor} — ${accidentalSummary(signature)}`;
}

/** The altered letters, in the order they are written on the staff. */
export function accidentalOrder(signature: KeySignature): Letter[] {
  if (signature.kind === 'sharp') return SHARP_ORDER.slice(0, signature.count);
  if (signature.kind === 'flat') return FLAT_ORDER.slice(0, signature.count);
  return [];
}
